// The step-forward (validate / continue) button gate for learner question steps.
//
// A question partial calls `wire()` with a predicate for "has the learner
// answered?" and the input events that can change the answer. On every such
// event — and once on wiring — it recomputes readiness and announces it as a
// `step-forward:answered` event on `document`, caching the last value so a
// listener that attaches later (the React FDA button, which mounts async) is
// not missed.
//
// On non-FDA pages the validate button is a plain server-rendered node, so
// `wire()` also toggles its `disabled` attribute and the header continue
// button's, looked up at event time and null-guarded. On FDA pages the React
// button carries the same id but is marked `data-react-owned`: `wire()` never
// touches it, the React button reads the event instead. This is what stops the
// render-order race (#8388): the inline script no longer mutates a React-owned
// DOM node.
//
// `wire()` returns `notify`, which recomputes and re-emits. Variants with
// bespoke triggers (a checkbox toggled from a keypress, a delegated listener)
// call it directly instead of declaring an `on` binding.

export const STEP_FORWARD_EVENT = 'step-forward:answered';

const FORWARD_BUTTON_ID = 'step-forward-button';
const HEADER_BUTTON_SELECTOR = '[data-id="step-forward-button"]';
// Set on the React FDA validate button, which manages its own `disabled`.
export const REACT_OWNED_ATTRIBUTE = 'data-react-owned';

declare global {
  interface Window {
    __stepForwardReady?: boolean;
  }
}

// The last readiness the page announced, for a listener that attaches after the
// first emit (React mounts async). `undefined` means nothing has wired yet.
export const readLastReady = (): boolean | undefined => window.__stepForwardReady;

// The forward button when it is server-rendered; null when absent or React-owned.
const serverForwardButton = (): HTMLElement | null => {
  const button = document.getElementById(FORWARD_BUTTON_ID);
  return button?.hasAttribute(REACT_OWNED_ATTRIBUTE) ? null : button;
};

const setDisabled = (element: Element | null, disabled: boolean): void => {
  if (!element) return;
  if (disabled) {
    element.setAttribute('disabled', 'true');
  } else {
    element.removeAttribute('disabled');
  }
};

const emit = (ready: boolean): void => {
  window.__stepForwardReady = ready;
  setDisabled(serverForwardButton(), !ready);
  setDisabled(document.querySelector(HEADER_BUTTON_SELECTOR), !ready);
  document.dispatchEvent(new CustomEvent(STEP_FORWARD_EVENT, { detail: { ready } }));
};

// One event binding: attach `event` to each element matching `selector`.
export type TriggerBinding = { selector: string; event?: string };

export type WireOptions = {
  // True when the learner has answered enough to validate.
  isReady: () => boolean;
  // Input events that recompute readiness. Omit for a variant that drives
  // `notify` itself.
  on?: TriggerBinding[];
  // When true, click the header continue button on a change that becomes ready
  // (used by the all-unique-choice audit step to advance to the next question).
  // Never fires on FDA pages: the header click is forwarded to the React
  // validate button and would open the signature modal by itself.
  autoAdvance?: boolean;
};

// Recomputes readiness and re-emits. Returned so bespoke triggers can call it.
export type Notify = () => void;

// Wire the readiness gate. Emits the current state immediately, then on every
// bound event. Safe to call before or after the React validate button mounts.
export const wire = ({ isReady, on = [], autoAdvance = false }: WireOptions): Notify => {
  const notify: Notify = () => {
    const ready = isReady();
    emit(ready);
    if (autoAdvance && ready && serverForwardButton()) {
      document.querySelector<HTMLElement>(HEADER_BUTTON_SELECTOR)?.click();
    }
  };

  on.forEach(({ selector, event = 'change' }) => {
    document.querySelectorAll(selector).forEach((trigger) => {
      trigger.addEventListener(event, notify);
    });
  });

  // Initial state — never auto-advances, so a restored answer does not submit
  // itself on load.
  emit(isReady());

  return notify;
};
